/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class UnionAPI
 * @hideconstructor
 * @classdesc Provides access to the the union object associated to this dataitem.
 */
define([
	'underscore',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../lib/@waca/dashboard-common/dist/core/UniqueHashIdBuilder',
	'./UnionAPISpec',
	'../../api/UnionAPI'
], ( _, APIFactory, UniqueHashIdBuilder, UnionAPISpec, UnionAPI) => {

	return class Union extends UnionAPISpec{

		constructor(unionModel, UnionDataItem, dataItem, dataSourceAPI, transaction, locale) {
			super();
			this.unionModel = unionModel;
			this.dataItem = dataItem;
			this._UnionDataItem = UnionDataItem;
			this.dataSourceAPI = dataSourceAPI;
			this.transaction = transaction;
			this.locale = locale;
			this._currentDataItemIdList = [];
			this.dataItemImplList = [];
			this._initalizeDataItems();
		}

		destroy() {
			this.unionModel = null;
			this.dataItem = null;
			this._UnionDataItem = null;
			this.dataSourceAPI = null;
			this.transaction = null;
			this.locale = null;
			this.dataItemImplList.forEach(dataItem => dataItem.destroy());
			this.dataItemImplList = [];
			this.dataItemAPIListCache = null;
			this._currentDataItemIdList = null;
		}

		_initalizeDataItems() {
			if (this.unionModel.dataItems){
				this.unionModel.dataItems.forEach(model => {
					this._createDataItemImpl(model);
				});
			}
		}

		_createDataItemImpl(model){
			if (!model.id){
				model.id = UniqueHashIdBuilder.createUniqueHashId(model.itemId, this._currentDataItemIdList);
			}
			this._currentDataItemIdList.push(model.id);
			this.dataItemAPIListCache = null;
			const dataItem = new this._UnionDataItem(model, this.dataSourceAPI, this.transaction, this.locale);
			APIFactory.setParentChildRelation(this, dataItem);
			this.dataItemImplList.push(dataItem);
			return dataItem;
		}

		getAPI() {
			return APIFactory.createAPI(this, [UnionAPI]);
		}

		getDataItemList() {
			if (!this.dataItemAPIListCache ){
				this.dataItemAPIListCache  = this.dataItemImplList.map(impl => impl.getAPI());
			}
			return this.dataItemAPIListCache;
		}

		getDataItemImpl(id){
			return this.dataItemImplList.find((dataItem) => {
				return dataItem.getId() === id;
			});
		}

		getDataItem(id){
			return this.getDataItemList().find((dataItem) => {
				return dataItem.getId() === id;
			});
		}

		_getItemId() {
			return this.dataItem.getColumnId();
		}

		addDataItem(dataItem){
			if (!this.unionModel.dataItems) {
				this.unionModel.dataItems = [];
			}
			const dataItemCollection = this.unionModel.dataItems;
			const model = (({ id, itemId, columnId, itemLabel, navigationPathId }) => ({ id, itemId, columnId, itemLabel, navigationPathId }))(dataItem);
			if (model.columnId){
				model.itemId = model.columnId;
				delete model.columnId;
			}
			dataItemCollection.push(model);
			return this._createDataItemImpl(model).getAPI();
		}

		removeDataItem(dataItemId) {
			if (this.unionModel.dataItems) {
				let index = -1;
				this.unionModel.dataItems.find((model, currentIndex) => {
					if (model.id === dataItemId){
						index = currentIndex;
						return true;
					}
					return false;
				});
				if (index != -1){
					this.unionModel.dataItems.splice(index, 1);
					this.dataItemImplList[index].destroy();
					this.dataItemImplList.splice(index, 1);
					this.dataItemAPIListCache.splice(index, 1);
					this._currentDataItemIdList.splice(index, 1);

				}
			}
		}

		setDataItems(dataItemPropsList, transactionToken) {
			const subTransaction = this.transaction.startTransaction(transactionToken);
			const currentIds = this.dataItemImplList.map(dataItem => dataItem.getId());
			// destroy the dataitem APIs
			currentIds.forEach(id => this.getAPI().removeDataItem(id, subTransaction));
			dataItemPropsList.forEach(dataItem => this.getAPI().addDataItem(dataItem, subTransaction));
			this.transaction.endTransaction(subTransaction);
			return this.getDataItemList();
		}
	};
});
